/**
 * SignupWa — alta/login passwordless por WhatsApp + OTP (espejo de ArchiCloud.SignupWa,
 * adaptado a DashFlow: Redux + JWT).
 *
 * Paso 1: el visitante deja su WhatsApp → POST /api/registration/phone/start (manda el OTP).
 * Paso 2: tipea el código de 6 dígitos → POST /api/registration/phone/verify → el backend
 * LOGUEA (si el teléfono ya tiene cuenta) o PROVISIONA una cuenta nueva, y devuelve el JWT.
 * Guardamos el token (vía phoneVerifyAsync) y entramos directo a la app.
 *
 * Captura utm_source/utm_medium/utm_campaign del query string para medir "anuncio → OTP signup".
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { RootState, AppDispatch } from '../store';
import { phoneVerifyAsync, clearError } from '../store/authSlice';
import { apiService } from '../services/api';
import { trackFunnel } from '../utils/tracking';
import { getPendingCoupon } from '../utils/pendingCoupon';
import {
  BrandMark,
  CREAM,
  DottedRule,
  INDIGO,
  INDIGO_DARK,
  INK,
  INK_SOFT,
  INTER_STACK,
  MONO_STACK,
  SkuKicker,
} from '../components/auth/TicketTape';

type Step = 'phone' | 'code';

const SignupWa: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const { loading, error, isAuthenticated, tenant } = useSelector((state: RootState) => state.auth);

  const utm = useMemo(
    () => ({
      utmSource: searchParams.get('utm_source') || undefined,
      utmMedium: searchParams.get('utm_medium') || undefined,
      utmCampaign: searchParams.get('utm_campaign') || undefined,
    }),
    [searchParams],
  );

  // Deep-link del OTP de WhatsApp (?phone=549...): pre-llenamos el teléfono y saltamos directo
  // al paso del código, SIN re-pedir uno (el código ya está en el mismo mensaje de WhatsApp).
  // Lo guardamos como local (sin 54/549); el backend lo re-normaliza a 549<área><número>.
  const prefillPhone = useMemo(() => {
    const digits = (searchParams.get('phone') || '').replace(/[^0-9]/g, '');
    if (digits.startsWith('549')) return digits.slice(3);
    if (digits.startsWith('54')) return digits.slice(2);
    return digits;
  }, [searchParams]);

  const [step, setStep] = useState<Step>(prefillPhone.length >= 8 ? 'code' : 'phone');
  const [phone, setPhone] = useState(prefillPhone);
  const [code, setCode] = useState('');
  const [isExisting, setIsExisting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const redirecting = useRef(false);
  // Durante el verify la navegación la hace handleVerify, después de mandar los eventos del embudo.
  const verifying = useRef(false);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Si ya quedó autenticado (p. ej. tras el verify), entrar a la app.
  useEffect(() => {
    if (!isAuthenticated || redirecting.current || verifying.current) return;
    redirecting.current = true;
    if (tenant && !tenant.onboardingCompletedAt) navigate('/completar-perfil');
    else navigate('/dashboard');
  }, [isAuthenticated, tenant, navigate]);

  const handleStart = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLocalError('');
    setInfo('');
    if (phone.replace(/[^0-9]/g, '').length < 8) {
      setLocalError('Ingresá un número de WhatsApp válido.');
      return;
    }
    setBusy(true);
    try {
      const data = await apiService.phoneStart({ phone, ...utm });
      setIsExisting(Boolean(data?.isExisting));
      // Embudo: Lead = se pidió el código para una cuenta nueva (si el teléfono ya tiene cuenta es un login).
      if (!data?.isExisting) trackFunnel('Lead');
      setStep('code');
      setInfo('Te enviamos un código por WhatsApp. Revisá tus mensajes.');
      // Dev only: el backend devuelve devCode en localhost para testear sin WhatsApp real.
      if (data?.devCode) setCode(String(data.devCode));
    } catch (err: any) {
      setLocalError(err.response?.data?.message || 'No pudimos enviar el código. Intentá de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLocalError('');
    if (code.trim().length < 4) {
      setLocalError('Ingresá el código que te enviamos.');
      return;
    }
    verifying.current = true;
    try {
      const res = await dispatch(phoneVerifyAsync({ phone, code: code.trim(), ...utm, couponCode: getPendingCoupon() || undefined })).unwrap();
      setInfo('¡Listo! Entrando a tu cuenta…');
      // Cuenta nueva = tenant en el plan Gratis (sin prueba ni tarjeta): alta y cuenta activa a la vez.
      if (!res.isExisting) await trackFunnel('CompleteRegistration', 'StartTrial');
      redirecting.current = true;
      if (res.tenant && !res.tenant.onboardingCompletedAt) navigate('/completar-perfil');
      else navigate('/dashboard');
    } catch {
      // El error queda en el slice (state.error).
    } finally {
      verifying.current = false;
    }
  };

  const resend = async () => {
    setCode('');
    await handleStart();
  };

  const alertMsg = localError || error;

  const primaryBtnSx = {
    py: 1.4,
    borderRadius: '5px',
    fontFamily: INTER_STACK,
    fontWeight: 600,
    fontSize: 15,
    textTransform: 'none' as const,
    bgcolor: INDIGO,
    color: '#fff',
    boxShadow: '0 1px 0 rgba(15,23,42,0.08)',
    '&:hover': {
      bgcolor: INDIGO_DARK,
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 16px rgba(30,64,175,0.22)',
    },
    '&:active': { transform: 'translateY(0)' },
  };

  const fieldSx = {
    fontFamily: INTER_STACK,
    '& .MuiOutlinedInput-root': {
      borderRadius: '5px',
      fontFamily: INTER_STACK,
      bgcolor: '#fff',
      '& fieldset': { borderColor: 'rgba(15,23,42,0.18)' },
      '&:hover fieldset': { borderColor: 'rgba(15,23,42,0.32)' },
      '&.Mui-focused fieldset': { borderColor: INDIGO, borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root': { fontFamily: INTER_STACK, color: INK_SOFT },
    '& .MuiInputLabel-root.Mui-focused': { color: INDIGO },
    '& .MuiFormHelperText-root': { fontFamily: INTER_STACK, color: INK_SOFT },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: CREAM,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: INTER_STACK,
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          bgcolor: '#fff',
          borderRadius: '10px',
          border: '1px solid rgba(15,23,42,0.12)',
          boxShadow: '0 8px 40px rgba(15,23,42,0.08)',
          p: { xs: 3, sm: 4 },
        }}
      >
        <Box sx={{ mb: 3 }}>
          <BrandMark color={INK} size="md" />
        </Box>

        <Box sx={{ mb: 2 }}>
          <SkuKicker label="LOTE · WHATSAPP-OTP" />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontFamily: INTER_STACK,
            fontSize: { xs: 24, sm: 27 },
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: INK,
            mb: 0.75,
          }}
        >
          {step === 'phone' ? 'Entrá con tu WhatsApp' : 'Ingresá el código'}
        </Typography>
        <Typography sx={{ fontFamily: INTER_STACK, fontSize: 14.5, color: INK_SOFT, mb: 1 }}>
          {step === 'phone'
            ? 'Sin contraseñas. Te mandamos un código por WhatsApp y entrás al instante.'
            : isExisting
              ? `Te reconocimos. Te mandamos un código a ${phone} para entrar a tu cuenta.`
              : `Te mandamos un código de 6 dígitos a ${phone}.`}
        </Typography>

        <DottedRule my={1.5} />

        {alertMsg && (
          <Alert severity="error" sx={{ mb: 2, mt: 1, fontFamily: INTER_STACK, borderRadius: '5px' }}>
            {alertMsg}
          </Alert>
        )}
        {info && !alertMsg && (
          <Alert severity="info" sx={{ mb: 2, mt: 1, fontFamily: INTER_STACK, borderRadius: '5px' }}>
            {info}
          </Alert>
        )}

        {step === 'phone' ? (
          <Box component="form" onSubmit={handleStart} sx={{ mt: 1 }}>
            <Stack spacing={2}>
              <TextField
                label="WhatsApp"
                placeholder="11 6807 8814"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                autoFocus
                inputMode="tel"
                disabled={busy}
                helperText="Con código de área. Argentina, sin el 0 ni el 15."
                sx={fieldSx}
              />
              <Button type="submit" variant="contained" size="large" fullWidth disableElevation disabled={busy} sx={primaryBtnSx}>
                {busy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Enviar código'}
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleVerify} sx={{ mt: 1 }}>
            <Stack spacing={2}>
              <TextField
                label="Código"
                placeholder="______"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                fullWidth
                autoFocus
                inputMode="numeric"
                disabled={loading}
                inputProps={{ maxLength: 6, style: { letterSpacing: '0.5em', fontWeight: 700 } }}
                sx={fieldSx}
              />
              <Button type="submit" variant="contained" size="large" fullWidth disableElevation disabled={loading} sx={primaryBtnSx}>
                {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Verificar y entrar'}
              </Button>
              <Stack direction="row" justifyContent="space-between">
                <Button
                  onClick={() => { setStep('phone'); setCode(''); setLocalError(''); setInfo(''); dispatch(clearError()); }}
                  disabled={loading || busy}
                  sx={{ color: INK_SOFT, textTransform: 'none', fontFamily: INTER_STACK }}
                >
                  Cambiar número
                </Button>
                <Button
                  onClick={resend}
                  disabled={loading || busy}
                  sx={{ color: INDIGO, textTransform: 'none', fontWeight: 600, fontFamily: INTER_STACK }}
                >
                  Reenviar código
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        <Box
          sx={{
            mt: 3,
            height: '1px',
            backgroundImage: `repeating-linear-gradient(to right, ${INK} 0, ${INK} 2px, transparent 2px, transparent 8px)`,
            opacity: 0.25,
          }}
        />
        <Typography sx={{ mt: 2, textAlign: 'center', fontFamily: INTER_STACK, fontSize: 14, color: INK_SOFT }}>
          ¿Preferís email?{' '}
          <Link
            component={RouterLink}
            to="/register"
            sx={{ fontFamily: INTER_STACK, color: INDIGO, fontWeight: 600, textDecoration: 'none', borderBottom: `1px dotted ${INDIGO}` }}
          >
            Creá tu cuenta →
          </Link>
        </Typography>
        <Typography sx={{ mt: 2, textAlign: 'center', fontFamily: MONO_STACK, fontSize: 11, color: INK_SOFT, opacity: 0.7 }}>
          Al continuar aceptás los términos y la política de privacidad.
        </Typography>
      </Box>
    </Box>
  );
};

export default SignupWa;
